import { useAppDispatch, useAppSelector, useLockBodyScroll } from '@/hooks';
import { setChipsModalOpen } from '@/store/slices/uiSlice';
import { Icons } from '@/utils/Icons';
import { useForm, useFieldArray } from 'react-hook-form';
import { useEffect } from 'react';
import { useGetStakesQuery, useUpdateStakesMutation } from '@/store/api/betApi';

interface Chip {
  name: string;
  value: number;
}

interface FormType {
  chips: Chip[];
}

const CustomChipsModal = () => {
  const { chipsModalOpen: open } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();

  const onClose = () => {
    dispatch(setChipsModalOpen(false));
  };

  /* ---------------- API ---------------- */
  const { data, isFetching } = useGetStakesQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: !open,
  });
  const [updateStakes, { isLoading }] = useUpdateStakesMutation();

  /* ---------------- FORM ---------------- */
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: { chips: [] },
  });

  const { fields } = useFieldArray({
    control,
    name: 'chips',
  });

  /* ---------------- Populate ---------------- */
  useEffect(() => {
    if (data?.stakeSet) {
      reset({
        chips: data.stakeSet.map((item: any) => ({
          name: item.label,
          value: item.value,
        })),
      });
    }
  }, [data, reset]);

  /* ---------------- Submit ---------------- */
  const onSubmit = async (formData: FormType) => {
    try {
      await updateStakes({
        data: formData.chips.map((chip) => ({
          label: chip.name,
          value: chip.value,
        })),
      }).unwrap();

      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  useLockBodyScroll(open);

  /* ---------------- Render ---------------- */
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] pt-[50px] flex justify-center bg-black/70">
      <div className="w-full max-w-3xl rounded-xl overflow-hidden bg-[#1c1d25] text-white shadow-xl h-fit">
        {/* HEADER */}
        <div className="flex items-center justify-between bg-yellow-500 px-5 py-3">
          <h2 className="font-semibold text-black text-lg">Custom Chips Setting</h2>

          <button onClick={onClose}>
            <Icons.Cross className="text-black" size={20} />
          </button>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
            {isFetching ? (
              <div>Loading...</div>
            ) : (
              fields.map((chip, index) => (
                <div key={chip.id} className="grid grid-cols-12 items-center gap-3">
                  {/* Index */}
                  <div className="col-span-1 text-center font-semibold">{index + 1}.</div>

                  {/* Name */}
                  <div className="col-span-5 grid grid-cols-12 items-center">
                    <span className="col-span-12 md:col-span-4 text-sm text-gray-300">Name :</span>
                    <input
                      {...register(`chips.${index}.name`, {
                        required: 'Name is required',
                        maxLength: {
                          value: 6,
                          message: 'Max 6 chars',
                        },
                        validate: (value) =>
                          /^[a-zA-Z0-9]*$/.test(value) || 'Only alphanumeric allowed',
                        onChange: (e) => {
                          e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                        },
                      })}
                      className="col-span-12 md:col-span-8 h-10 rounded bg-secondary px-3 text-sm outline-none"
                    />
                    {errors?.chips?.[index]?.name && (
                      <span className="text-red-500 text-xs col-span-12">
                        {errors.chips[index]?.name?.message}
                      </span>
                    )}
                  </div>

                  {/* Value */}
                  <div className="col-span-5 grid grid-cols-12 items-center">
                    <span className="col-span-12 md:col-span-4 text-sm text-gray-300">Value :</span>
                    <input
                      type="number"
                      {...register(`chips.${index}.value`, {
                        valueAsNumber: true,
                        required: 'Value is required',
                        min: {
                          value: 1,
                          message: 'Must be > 0',
                        },
                      })}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      className="col-span-12 md:col-span-8 h-10 rounded bg-secondary px-3 text-sm outline-none"
                    />
                    {errors?.chips?.[index]?.value && (
                      <span className="text-red-500 text-xs col-span-12">
                        {errors.chips[index]?.value?.message}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* FOOTER */}
          <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-700">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-green-500 hover:bg-green-600 px-5 py-2 rounded text-sm font-medium"
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="bg-red-500 hover:bg-red-600 px-5 py-2 rounded text-sm font-medium"
            >
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomChipsModal;
